import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Grid, Typography } from '@mui/material';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { capitalizeName } from 'common/utils/titleCase';
import { getPartners } from 'store/thunks/partnerThunks';

import EditCatalogAvailabilityDialog from './EditCatalogAvailabilityDialog';

const EMPTY_ARRAY = [];

const CatalogAvailabilitySection = ({ item, drugName }) => {
  const dispatch = useDispatch();

  const currentTenantId = useSelector(({ auth }) => auth.currentTenantId);
  const allPartners = useSelector(({ partner }) => partner[currentTenantId] ?? EMPTY_ARRAY);

  // Cached in the store (same source PartnerNdcPreference and others use), so
  // this is a no-op once something else on the page has already loaded it.
  useEffect(() => {
    if (allPartners.length === 0) dispatch(getPartners());
  }, [dispatch, allPartners.length]);

  const whitelisted = item.whitelisted ?? EMPTY_ARRAY;

  const isAvailableForAllPartners =
    item.isAvailable &&
    (isEmpty(whitelisted) ||
      (!isEmpty(allPartners) &&
        allPartners.every(({ partnerUuid }) => whitelisted.includes(partnerUuid))));

  const whitelistedNames = whitelisted
    .map(
      (partnerUuid) =>
        allPartners.find((partner) => partner.partnerUuid === partnerUuid)?.partnerName
    )
    .filter(Boolean)
    .map(capitalizeName)
    .sort((left, right) =>
      String(left).localeCompare(String(right), undefined, { sensitivity: 'base' })
    );

  return (
    <Box>
      <Grid container justifyContent='space-between' alignItems='center'>
        <Typography variant='h6' component='h3' color='primary'>
          Availability
        </Typography>

        <EditCatalogAvailabilityDialog item={item} drugName={drugName} allPartners={allPartners} />
      </Grid>

      <Box sx={{ p: 2 }}>
        <Grid container alignItems='center'>
          <Typography sx={{ width: 150 }} color='primary'>
            Available for all partners
          </Typography>

          {isAvailableForAllPartners ? <CheckIcon color='success' /> : <CloseIcon color='error' />}
        </Grid>

        {!isAvailableForAllPartners && !isEmpty(whitelisted) && (
          <Grid container alignItems='center' sx={{ my: 2 }}>
            <Typography sx={{ width: 150 }} color='primary'>
              Whitelisted Partners
            </Typography>

            <Typography variant='body2'>{whitelistedNames.join(', ')}</Typography>
          </Grid>
        )}
      </Box>
    </Box>
  );
};

CatalogAvailabilitySection.propTypes = {
  item: PropTypes.shape({
    catalogId: PropTypes.string.isRequired,
    isAvailable: PropTypes.bool,
    isPartnerActive: PropTypes.bool,
    whitelisted: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  drugName: PropTypes.string.isRequired,
};

export default CatalogAvailabilitySection;
